import { getLogger } from 'utils/datadog-logger';

const logger = getLogger('data-layer');

// A third-party interceptor that breaks `dataLayer.push` breaks it for *every*
// subsequent push, and cart/page/product events are high frequency — logging each
// one would turn a single bad container into hundreds of indexed Datadog errors per
// session. Log one error per distinct message+error signature per page load instead.
const loggedSignatures = new Set<string>();

// JavaScript permits `throw null`, `throw 'string'`, and `throw {}`, so reading
// `.message` off a caught value can itself throw and restore the crash path this
// logging exists to prevent.
function toMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

// The context carries the identity of the thing that failed (container ID, sGTM loader
// URL), so it belongs in the key: two containers throwing the same message are two
// distinct problems, and suppressing the second would hide it from Datadog entirely.
function toSignature(message: string, errorMessage: string, context: Record<string, unknown>): string {
  const contextKey = Object.keys(context)
    .sort()
    .map((key) => `${key}=${String(context[key])}`)
    .join('|');

  return `${message}|${errorMessage}|${contextKey}`;
}

/**
 * Logs a swallowed `dataLayer.push` failure, deduplicated for the life of the page.
 * Safe to call from inside a catch block with an arbitrary thrown value.
 */
export function logDataLayerPushError(message: string, error: unknown, context: Record<string, unknown> = {}): void {
  const errorMessage = toMessage(error);
  const signature = toSignature(message, errorMessage, context);

  if (loggedSignatures.has(signature)) {
    return;
  }
  loggedSignatures.add(signature);

  logger.error(message, { ...context, error: errorMessage });
}

/** Clears the dedupe cache. For tests only — the cache is otherwise page-lifetime. */
export function resetDataLayerPushErrorLog(): void {
  loggedSignatures.clear();
}
